/**
 * Calendar availability types for booking flow
 * Based on /api/calendar-availability-new endpoint
 */

import type { ServiceType } from '../state/types';

/**
 * Calendar day type constant for early access / request days.
 * Used to detect when a date triggers the early access flow.
 */
export const CALENDAR_DAY_TYPE_REQUEST: CalendarDayType = 'request';

/**
 * Calendar day availability type
 * Determines how the day is displayed in the calendar
 */
export type CalendarDayType =
  | 'available'              // Day with normal availability
  | 'request'                // Day with request/early access
  | 'waiting-list'           // Only waiting list available
  | 'walk-in'                // Only walk-in available
  | 'not-available'          // No availability (but not closed)
  | 'not-available-closed'   // Restaurant is closed
  | 'not-available-old-day'; // Past day

/**
 * Single day in the calendar availability response
 */
export interface CalendarDayInfo {
  /** ISO date string (e.g., "2025-10-04T00:00:00+00:00") */
  date: string;
  /** Whether the day has availability. */
  isAvailable: boolean;
  /** Whether the day has preferred-access availability. */
  isAvailablePreferred: boolean;
  /** Whether the waiting list is full. */
  waitingListFull: boolean;
  /** Whether this is a special day (event, holiday, etc.) */
  isSpecialDay: boolean;
  /** Whether to highlight the special day in UI */
  highlightSpecialDay: boolean;
  /** Type of availability for this day */
  type: CalendarDayType;
  /** Center hour for display purposes */
  centerHour: string;
  /** Minimum order amount for delivery/takeaway */
  minimumOrderAmount: number | null;
}

/**
 * A day exactly as the endpoint sends it, before normalization.
 *
 * The three flags are `unknown` because the endpoint is inconsistent about their
 * shape — a single response has been observed carrying an int, a string and a
 * real boolean across different fields at once. Typing them here rather than on
 * CalendarDayInfo is what forces fetchCalendarAvailability to coerce: annotate
 * its api.get with this type and dropping the coercion stops compiling. It makes
 * the right type available and obvious at a new calendar endpoint — it cannot
 * make the wrong one unreachable, since a future api.get<CalendarAvailabilityResponse>
 * would still compile. CalendarDayInfo is publicly re-exported, so reach for
 * this one whenever you are typing a response rather than a coerced day.
 */
export interface RawCalendarDayInfo
  extends Omit<CalendarDayInfo, 'isAvailable' | 'isAvailablePreferred' | 'waitingListFull'> {
  isAvailable?: unknown;
  isAvailablePreferred?: unknown;
  waitingListFull?: unknown;
}

/** Raw response from /api/calendar-availability-new, before normalization. */
export interface RawCalendarAvailabilityResponse {
  /** Optional because responses omitting it have been observed. */
  calendarInfo?: RawCalendarDayInfo[];
}

/**
 * Response from /api/calendar-availability-new, after fetchCalendarAvailability
 * has normalized it. Flags are real booleans and calendarInfo is always present.
 */
export interface CalendarAvailabilityResponse {
  /** Days with availability info. */
  calendarInfo: CalendarDayInfo[];
}

/**
 * Calendar availability with service type attached
 */
export interface ServiceCalendarAvailability extends CalendarAvailabilityResponse {
  /** The service type this calendar belongs to */
  serviceType: ServiceType;
}

/**
 * Display type for visual styling (simplified from CalendarDayType)
 */
export type AvailabilityDisplayType =
  | 'available'      // Green, clickable
  | 'request'        // Green, requires approval
  | 'waiting-list'   // Yellow/orange, limited
  | 'walk-in'        // Blue, walk-in only
  | 'not-available'  // Gray, disabled
  | 'closed';        // Dark gray with strikethrough, disabled

/**
 * Merged calendar availability for all services
 * Key is the date (YYYY-MM-DD), value contains availability per service
 */
export interface MergedCalendarDay {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Raw service data */
  services: {
    [K in ServiceType]?: CalendarDayInfo;
  };
  /** Whether any service has availability */
  hasAnyAvailability: boolean;
  /** Whether any service has preferred-only availability */
  hasPreferredAvailability: boolean;
  /** Whether waiting list is full across all services with availability (AND logic) */
  isWaitingListFull: boolean;
  /** List of service types that are available (not closed) */
  availableServiceTypes: ServiceType[];
  /** Merged type for display (best available across all services) */
  type: CalendarDayType;
  /** Display type for visual styling */
  displayType: AvailabilityDisplayType;
  /** Whether this is a special day (event, holiday, etc.) */
  isSpecialDay: boolean;
  /** Whether to highlight the special day with star icon */
  highlightSpecialDay: boolean;
  /** Center hour for display */
  centerHour: string;
  /** Minimum order amount (for delivery/takeaway) */
  minimumOrderAmount: number | null;
}

/**
 * Result of the useCalendarAvailability hook
 */
export interface CalendarAvailabilityResult {
  /** Raw calendar data per service */
  calendarByService: ServiceCalendarAvailability[];
  /** Merged calendar with all services */
  mergedCalendar: MergedCalendarDay[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
}

/**
 * Parameters for calendar availability query
 */
export interface CalendarAvailabilityParams {
  restaurantId: number;
  partySize: number;
  serviceTypes: ServiceType[];
}
